// ============================================================================
// CONFIG SERVICE
// ============================================================================
// Business logic and API calls for Configuration Framework module

import { get, put } from '../client/axiosClient';
import { configEndpoints } from '../endpoints/config.endpoints';
import type { ApiResponse } from '../types';
import type {
  ConfigGroup,
  ConfigDefinition,
  ConfigEffectiveValue,
  ConfigScope,
  ModuleSwitch,
  UpdateConfigRequest,
} from '../types/config.types';

/**
 * Build scopes query string from scope pairs.
 * @example buildScopesParam([{ type: 'PROPERTY', id: '...' }]) => 'PROPERTY:guid'
 */
const buildScopesParam = (
  scopes: Array<{ type: string; id: string }>
): string => {
  return scopes
    .filter((s) => s.id)
    .map((s) => `${s.type}:${s.id}`)
    .join(',');
};

/**
 * Config Service Class
 */
class ConfigService {
  /**
   * Get all config groups
   */
  async getGroups(): Promise<ApiResponse<ConfigGroup[]>> {
    const response = await get<ApiResponse<ConfigGroup[]>>(
      configEndpoints.groups()
    );
    return response.data;
  }

  /**
   * Module switches (Setup > Property & Rooms > Modules) and where each is on
   */
  async getModuleSwitches(): Promise<ModuleSwitch[]> {
    const response = await get<ApiResponse<ModuleSwitch[]>>(configEndpoints.modules());
    return response.data.data ?? [];
  }

  /**
   * Get config definitions for a group
   */
  async getDefinitions(
    group: string
  ): Promise<ApiResponse<ConfigDefinition[]>> {
    const response = await get<ApiResponse<ConfigDefinition[]>>(
      configEndpoints.definitions(group)
    );
    return response.data;
  }

  /**
   * Get effective config values for a group with scope resolution
   */
  async getEffectiveConfig(
    group: string,
    scopes?: Array<{ type: string; id: string }>
  ): Promise<ApiResponse<ConfigEffectiveValue[]>> {
    const scopesParam = scopes ? buildScopesParam(scopes) : undefined;
    const response = await get<ApiResponse<ConfigEffectiveValue[]>>(
      configEndpoints.effective(group, scopesParam)
    );
    return response.data;
  }

  /**
   * Get a single effective config value by key
   */
  async getEffectiveValue(
    configKey: string,
    scopes?: Array<{ type: string; id: string }>
  ): Promise<ApiResponse<{ configKey: string; effectiveValue: string }>> {
    const scopesParam = scopes ? buildScopesParam(scopes) : undefined;
    const response = await get<
      ApiResponse<{ configKey: string; effectiveValue: string }>
    >(configEndpoints.effectiveByKey(configKey, scopesParam));
    return response.data;
  }

  /**
   * Update config overrides at a specific scope
   */
  async updateValues(
    request: UpdateConfigRequest
  ): Promise<ApiResponse<{ updated: number }>> {
    const response = await put<ApiResponse<{ updated: number }>>(
      configEndpoints.updateValues(),
      request
    );
    return response.data;
  }

  /**
   * Get scope entries by type
   */
  async getScopes(scopeType: string): Promise<ApiResponse<ConfigScope[]>> {
    const response = await get<ApiResponse<ConfigScope[]>>(
      configEndpoints.scopes(scopeType)
    );
    return response.data;
  }
}

/**
 * Export singleton instance
 */
export const configService = new ConfigService();
export default configService;
